import { useGetClipboardScoreProfile } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { useCurrentDate } from "@src/appV2/lib/utils/useCurrentDate";
import {
  type BookabilityStatusItem,
  BookabilityUnmetCriteria,
} from "@src/appV2/OpenShifts/ShiftAction";
import { type ComputedArrivalData } from "@src/appV2/redesign/ArrivalTime/computeArrivalData";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { minutesToMilliseconds } from "date-fns";
import type React from "react";

import { useHasTen99PolicyCoverage } from "../../Documents/api/useHasTen99PolicyCoverage";
import { useGetBookingRequests } from "../../WorkerShifts/BookingRequests/api/useGetBookingRequests";
import { useWorkplaceHasBookingRequestsEnabled } from "../../WorkerShifts/BookingRequests/api/useWorkplaceHasBookingRequestsEnabled";
import { BookingRequestStatus } from "../../WorkerShifts/BookingRequests/types";
import { type BlockingRequirementsContext } from "../Bookability/Documents/types";
import { isBookabilityAllowedWithBackgroundCheck } from "../Bookability/isBookabilityAllowedWithBackgroundCheck";
import { type ShiftType } from "../constants";
import { type Window } from "../Open/types";
import { type ModalWorkplaceData, type WindowRestriction } from "../types";
import { type CardProps, CARDS, ShiftBottomSheetBookabilityCard } from "./BookabilityCard";
import { ShiftBottomSheetCardsWrapper } from "./CardsWrapper";
import { useRenderedBookabilityCardTracking } from "./useRenderedBookabilityCardTracking";

export interface ShiftBottomSheetBookabilityCardsContainerProps {
  shiftId: string;
  shiftType: ShiftType;
  shiftQualification: string;
  workplace: ModalWorkplaceData;
  bookabilityStatus: BookabilityStatusItem;
  blockingRequirementsContext?: BlockingRequirementsContext;
  hasMarkedInterest?: boolean;
  shiftFailedLoading: boolean;
  hasHoursRestrictionConflict: boolean;
  shiftStartIso?: string;
  cardsToSkip?: ShiftBottomSheetBookabilityCard[];
  children?: React.ReactNode;
  restrictedToWindow?: WindowRestriction;
  isIndependentContractorBadgesRequired?: boolean;
  priorityTill?: Date;
  window?: Window;
  isShortLeadTime?: boolean;
  computedArrivalData?: ComputedArrivalData;
}

export function ShiftBottomSheetBookabilityCardsContainer(
  props: ShiftBottomSheetBookabilityCardsContainerProps
) {
  const {
    shiftId,
    shiftType,
    shiftQualification,
    workplace,
    bookabilityStatus,
    blockingRequirementsContext,
    hasMarkedInterest,
    shiftFailedLoading,
    hasHoursRestrictionConflict,
    shiftStartIso,
    cardsToSkip = [],
    children,
    restrictedToWindow,
    isIndependentContractorBadgesRequired,
    priorityTill,
    window,
    isShortLeadTime,
    computedArrivalData,
  } = props;

  // Refresh this component every minute to get the latest priority status
  useCurrentDate(minutesToMilliseconds(1));

  const isBookable = isBookabilityAllowedWithBackgroundCheck(bookabilityStatus);

  const worker = useDefinedWorker();
  const { userId: workerId } = worker;
  const { bookingRequestsEnabled: shouldShowBookingRequests } =
    useWorkplaceHasBookingRequestsEnabled(workplace.id);

  const { data: { clipboardScore } = {} } = useGetClipboardScoreProfile(workerId, {
    enabled: Boolean(workerId),
  });

  const { data: bookingRequests } = useGetBookingRequests(
    {
      filter: {
        status: { eq: BookingRequestStatus.PENDING },
        "worker.id": { eq: workerId },
        "shift.id": { eq: shiftId },
      },
    },
    {
      enabled:
        shouldShowBookingRequests &&
        !cardsToSkip.includes(ShiftBottomSheetBookabilityCard.SHIFT_REQUESTED),
    }
  );
  const hasRequestedShift = (bookingRequests?.data.length ?? 0) > 0;

  const { handleRenderDecision } = useRenderedBookabilityCardTracking({
    bookabilityStatus,
    ignoreUnmetCriteria: [BookabilityUnmetCriteria.WORKER_MISSING_REQUIREMENTS],
  });

  const hasTen99PolicyCoverage = useHasTen99PolicyCoverage({
    shiftQualification,
    workplaceId: workplace.id,
  });

  const cardProps: CardProps = {
    shiftId,
    shiftType,
    shiftQualification,
    workplace,
    bookabilityStatus,
    blockingRequirementsContext,
    hasMarkedInterest,
    shiftFailedLoading,
    hasHoursRestrictionConflict,
    shiftStartIso,
    isBookable,
    canSeeBookingRequests: shouldShowBookingRequests,
    hasRequestedShift,
    cardsToSkip,
    worker,
    onRenderDecision: handleRenderDecision,
    restrictedToWindow,
    score: clipboardScore,
    isIndependentContractorBadgesRequired,
    priorityTill,
    window,
    isShortLeadTime,
    computedArrivalData,
    workplaceTimezone: workplace.attributes.location.timezone,
    hasTen99PolicyCoverage,
  };

  return (
    <ShiftBottomSheetCardsWrapper>
      {CARDS.map((Card) => (
        <Card key={Card.name} {...cardProps} />
      ))}
      {children}
    </ShiftBottomSheetCardsWrapper>
  );
}
